import { useState } from "react";
import { createExternalAccount } from "../services/gridApi";
import { useDemo } from "../context/DemoContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName?: string;
  onCreated: () => void;
}

type AccountType =
  | "USD_ACCOUNT" | "EUR_ACCOUNT" | "MXN_ACCOUNT" | "BRL_ACCOUNT" | "INR_ACCOUNT"
  | "GBP_ACCOUNT" | "CAD_ACCOUNT" | "NGN_ACCOUNT" | "PHP_ACCOUNT" | "SGD_ACCOUNT"
  | "SPARK_WALLET" | "LIGHTNING" | "SOLANA_WALLET" | "BASE_WALLET" | "TRON_WALLET" | "POLYGON_WALLET";

interface AccountTypeConfig {
  label: string;
  currency: string;
  fields: { key: string; label: string; placeholder: string; required: boolean }[];
  needsBeneficiary: boolean;
}

const ACCOUNT_TYPES: Record<AccountType, AccountTypeConfig> = {
  USD_ACCOUNT: {
    label: "US Bank Account",
    currency: "USD",
    fields: [
      { key: "accountNumber", label: "Account Number", placeholder: "123456789", required: true },
      { key: "routingNumber", label: "Routing Number (9 digits)", placeholder: "987654321", required: true },
      { key: "bankName", label: "Bank Name", placeholder: "Chase Bank", required: false },
    ],
    needsBeneficiary: true,
  },
  EUR_ACCOUNT: {
    label: "IBAN (Europe)",
    currency: "EUR",
    fields: [
      { key: "iban", label: "IBAN", placeholder: "DE89370400440532013000", required: true },
      { key: "swiftBic", label: "SWIFT/BIC Code", placeholder: "DEUTDEFF", required: true },
    ],
    needsBeneficiary: true,
  },
  CAD_ACCOUNT: {
    label: "Canadian Account",
    currency: "CAD",
    fields: [
      { key: "accountNumber", label: "Account Number (7-12 digits)", placeholder: "1234567", required: true },
      { key: "branchCode", label: "Transit/Branch Code (5 digits)", placeholder: "00012", required: true },
      { key: "bankCode", label: "Institution Number (3 digits)", placeholder: "001", required: true },
    ],
    needsBeneficiary: true,
  },
  GBP_ACCOUNT: {
    label: "UK Bank Account",
    currency: "GBP",
    fields: [
      { key: "accountNumber", label: "Account Number (8 digits)", placeholder: "12345678", required: true },
      { key: "sortCode", label: "Sort Code (6 digits)", placeholder: "123456", required: true },
      { key: "bankName", label: "Bank Name", placeholder: "Barclays", required: false },
    ],
    needsBeneficiary: true,
  },
  MXN_ACCOUNT: {
    label: "CLABE (Mexico)",
    currency: "MXN",
    fields: [
      { key: "clabeNumber", label: "CLABE Number (18 digits)", placeholder: "123456789012345678", required: true },
    ],
    needsBeneficiary: true,
  },
  BRL_ACCOUNT: {
    label: "PIX (Brazil)",
    currency: "BRL",
    fields: [
      { key: "pixKey", label: "PIX Key", placeholder: "55119876543210", required: true },
      { key: "taxId", label: "Tax ID (CPF/CNPJ)", placeholder: "12345678901", required: true },
    ],
    needsBeneficiary: true,
  },
  INR_ACCOUNT: {
    label: "UPI (India)",
    currency: "INR",
    fields: [
      { key: "vpa", label: "Virtual Payment Address (VPA)", placeholder: "somecustomer@okbank", required: true },
    ],
    needsBeneficiary: true,
  },
  NGN_ACCOUNT: {
    label: "Nigerian Account",
    currency: "NGN",
    fields: [
      { key: "accountNumber", label: "Account Number (10 digits)", placeholder: "0123456789", required: true },
      { key: "bankName", label: "Bank Name", placeholder: "First Bank of Nigeria", required: true },
      { key: "purposeOfPayment", label: "Purpose of Payment", placeholder: "GIFT", required: true },
    ],
    needsBeneficiary: true,
  },
  PHP_ACCOUNT: {
    label: "Philippine Account",
    currency: "PHP",
    fields: [
      { key: "accountNumber", label: "Account Number", placeholder: "001234567890", required: true },
      { key: "bankName", label: "Bank Name", placeholder: "BDO Unibank", required: true },
    ],
    needsBeneficiary: true,
  },
  SGD_ACCOUNT: {
    label: "Singapore Account",
    currency: "SGD",
    fields: [
      { key: "accountNumber", label: "Account Number", placeholder: "0123456789", required: true },
      { key: "swiftCode", label: "SWIFT/BIC Code (8 or 11 chars)", placeholder: "DBSSSGSG", required: true },
      { key: "bankName", label: "Bank Name", placeholder: "DBS Bank Ltd", required: true },
    ],
    needsBeneficiary: true,
  },
  SPARK_WALLET: {
    label: "Spark Wallet",
    currency: "BTC",
    fields: [
      { key: "address", label: "Spark Wallet Address", placeholder: "spark1pgssyuu...", required: true },
    ],
    needsBeneficiary: false,
  },
  LIGHTNING: {
    label: "Lightning",
    currency: "BTC",
    fields: [
      { key: "lightningAddress", label: "Lightning Address", placeholder: "user@wallet.com", required: true },
    ],
    needsBeneficiary: false,
  },
  SOLANA_WALLET: {
    label: "Solana Wallet",
    currency: "USDC",
    fields: [
      { key: "address", label: "Solana Address", placeholder: "4Nd1m6Qk...", required: true },
    ],
    needsBeneficiary: false,
  },
  TRON_WALLET: {
    label: "Tron Wallet",
    currency: "USDC",
    fields: [
      { key: "address", label: "Tron Address", placeholder: "T9yD14Nj...", required: true },
    ],
    needsBeneficiary: false,
  },
  POLYGON_WALLET: {
    label: "Polygon Wallet",
    currency: "USDC",
    fields: [
      { key: "address", label: "Polygon Address", placeholder: "0xAbCDEF...", required: true },
    ],
    needsBeneficiary: false,
  },
  BASE_WALLET: {
    label: "Base Wallet",
    currency: "USDC",
    fields: [
      { key: "address", label: "Base Address", placeholder: "0xAbCDEF...", required: true },
    ],
    needsBeneficiary: false,
  },
};

const SAMPLE_DATA: Record<AccountType, Record<string, string>> = {
  USD_ACCOUNT: { accountNumber: "123456789", routingNumber: "987654321", bankName: "Chase Bank" },
  EUR_ACCOUNT: { iban: "DE89370400440532013000", swiftBic: "DEUTDEFF" },
  CAD_ACCOUNT: { accountNumber: "1234567", branchCode: "00012", bankCode: "001" },
  GBP_ACCOUNT: { accountNumber: "12345678", sortCode: "20-00-00" },
  MXN_ACCOUNT: { clabeNumber: "123456789012345678" },
  BRL_ACCOUNT: { pixKey: "55119876543210", taxId: "12345678901" },
  INR_ACCOUNT: { vpa: "somecustomer@okbank" },
  NGN_ACCOUNT: { accountNumber: "0123456789", bankName: "First Bank of Nigeria", purposeOfPayment: "GIFT" },
  PHP_ACCOUNT: { accountNumber: "001234567890", bankName: "BDO Unibank" },
  SGD_ACCOUNT: { accountNumber: "0123456789", swiftCode: "DBSSSGSG", bankName: "DBS Bank Ltd" },
  SPARK_WALLET: { address: "spark1pgssyuuuhnrrdjswal5c3s3rafw9w3y5dd4cjy3duxlf7hjzkp0rqx6dj6mrhu" },
  LIGHTNING: { lightningAddress: "demo@lightningwallet.com" },
  SOLANA_WALLET: { address: "4Nd1m6Qkq7RfKuE5vQ9qP9Tn6H94Ueqb4xXHzsAbd8Wg" },
  TRON_WALLET: { address: "T9yD14NjWJc5RfKuE5vQ9qP9Tn6H94Ue" },
  POLYGON_WALLET: { address: "0xAbCDEF1234567890aBCdEf1234567890ABcDef12" },
  BASE_WALLET: { address: "0xAbCDEF1234567890aBCdEf1234567890ABcDef12" },
};

export default function CreateExternalAccountModal({ isOpen, onClose, customerId, customerName, onCreated }: Props) {
  const { demo, demoCreateExternalAccount } = useDemo();
  const [accountType, setAccountType] = useState<AccountType>("USD_ACCOUNT");
  const [fields, setFields] = useState<Record<string, string>>(SAMPLE_DATA["USD_ACCOUNT"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const config = ACCOUNT_TYPES[accountType];

  const handleTypeChange = (type: AccountType) => {
    setAccountType(type);
    setFields(SAMPLE_DATA[type]);
    setError("");
  };

  // Map account types to their countries and payment rails (required by backend)
  const ACCOUNT_META: Record<string, { countries: string[]; paymentRails: string[] }> = {
    USD_ACCOUNT: { countries: ["US"], paymentRails: ["ACH"] },
    EUR_ACCOUNT: { countries: ["DE", "FR", "IT", "ES", "NL"], paymentRails: ["SEPA"] },
    GBP_ACCOUNT: { countries: ["GB"], paymentRails: ["FASTER_PAYMENTS"] },
    CAD_ACCOUNT: { countries: ["CA"], paymentRails: ["EFT"] },
    MXN_ACCOUNT: { countries: ["MX"], paymentRails: ["SPEI"] },
    BRL_ACCOUNT: { countries: ["BR"], paymentRails: ["PIX"] },
    INR_ACCOUNT: { countries: ["IN"], paymentRails: ["UPI"] },
    NGN_ACCOUNT: { countries: ["NG"], paymentRails: ["NIP"] },
    PHP_ACCOUNT: { countries: ["PH"], paymentRails: ["INSTAPAY"] },
    SGD_ACCOUNT: { countries: ["SG"], paymentRails: ["PAYNOW"] },
  };

  const buildAccountInfo = (): Record<string, unknown> => {
    const info: Record<string, unknown> = { accountType, ...fields };

    // Add countries and paymentRails (required by backend for bank accounts)
    const meta = ACCOUNT_META[accountType];
    if (meta) {
      info.countries = meta.countries;
      info.paymentRails = meta.paymentRails;
    }

    if (accountType === "USD_ACCOUNT") {
      info.accountCategory = "CHECKING";
    }

    if (accountType === "BRL_ACCOUNT") {
      info.pixKeyType = "PHONE";
    }

    if (config.needsBeneficiary) {
      info.beneficiary = {
        beneficiaryType: "INDIVIDUAL",
        fullName: customerName || "John Doe",
        birthDate: "1990-01-15",
        nationality: "US",
        address: {
          line1: "123 Main Street",
          city: "San Francisco",
          postalCode: "94105",
          country: "US",
        },
      };
    }

    return info;
  };

  const buildAccountLabel = (): string => {
    if (accountType === "USD_ACCOUNT") return `${fields.bankName || "Bank"} ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "EUR_ACCOUNT") return `IBAN ****${(fields.iban || "").slice(-4)}`;
    if (accountType === "CAD_ACCOUNT") return `CA Bank ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "GBP_ACCOUNT") return `UK Bank ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "MXN_ACCOUNT") return `CLABE ****${(fields.clabeNumber || "").slice(-4)}`;
    if (accountType === "BRL_ACCOUNT") return `PIX ${fields.pixKey || ""}`;
    if (accountType === "INR_ACCOUNT") return `UPI ${fields.vpa || ""}`;
    if (accountType === "NGN_ACCOUNT") return `${fields.bankName || "NG Bank"} ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "PHP_ACCOUNT") return `${fields.bankName || "PH Bank"} ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "SGD_ACCOUNT") return `${fields.bankName || "SG Bank"} ****${(fields.accountNumber || "").slice(-4)}`;
    if (accountType === "LIGHTNING") return fields.lightningAddress || "Lightning";
    return `${accountType.replace(/_/g, " ")} ${(fields.address || "").slice(0, 8)}...`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (demo.enabled) {
      const result = demoCreateExternalAccount({
        customerId,
        currency: config.currency,
        accountType,
        accountLabel: buildAccountLabel(),
      });
      if (result.success) {
        onCreated();
        onClose();
      } else {
        setError(result.error || "Failed to create account");
      }
      setLoading(false);
      return;
    }

    try {
      await createExternalAccount({
        customerId,
        currency: config.currency,
        accountInfo: buildAccountInfo(),
      });
      onCreated();
      onClose();
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setError((e.message || "Failed to create external account") + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111111] border border-[#222222] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#222222] sticky top-0 bg-[#111111] z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#F8F8F7]">Add External Account</h2>
              <p className="text-xs text-[#00B3E0] font-mono mt-0.5">POST /customers/external-accounts</p>
            </div>
            <button onClick={onClose} className="text-[#8A8A8A] hover:text-[#F8F8F7]">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <pre className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm whitespace-pre-wrap">
              {error}
            </pre>
          )}

          {/* Account Type */}
          <div>
            <label className="block text-xs text-[#8A8A8A] mb-2">Account Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(ACCOUNT_TYPES) as AccountType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeChange(type)}
                  className={`p-2.5 rounded-lg border text-left text-xs ${
                    accountType === type
                      ? "border-[#00B3E0] bg-[#00B3E0]/5 text-[#00B3E0]"
                      : "border-[#333333] text-[#8A8A8A] hover:border-[#555555]"
                  }`}
                >
                  <div className="font-medium">{ACCOUNT_TYPES[type].label}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">{ACCOUNT_TYPES[type].currency}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Currency */}
          <div className="bg-[#0A1628] border border-[#1A3A5C] rounded-lg p-3">
            <div className="flex justify-between text-xs">
              <span className="text-[#8A8A8A]">Currency</span>
              <span className="text-[#00B3E0] font-mono font-medium">{config.currency}</span>
            </div>
            {config.needsBeneficiary && (
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-[#8A8A8A]">Beneficiary</span>
                <span className="text-[#CCCCCC]">Auto-filled from customer</span>
              </div>
            )}
          </div>

          {/* Dynamic Fields */}
          {config.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs text-[#8A8A8A] mb-1">
                {f.label} {f.required && <span className="text-red-400">*</span>}
              </label>
              <input
                type="text"
                value={fields[f.key] || ""}
                onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                required={f.required}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
              />
            </div>
          ))}

          {/* Request Preview */}
          <details>
            <summary className="text-xs text-[#00B3E0] cursor-pointer">Preview API request</summary>
            <pre className="mt-2 text-[10px] font-mono text-[#CCCCCC] bg-[#0A0A0A] p-3 rounded-lg overflow-auto max-h-48">
              {JSON.stringify({ customerId, currency: config.currency, accountInfo: buildAccountInfo() }, null, 2)}
            </pre>
          </details>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-[#333333] text-[#F8F8F7] py-2.5 rounded-lg hover:bg-[#222222]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
